import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Menu,
  ShoppingCart,
  MoreVertical,
  LogOut,
  HelpCircle,
  ClipboardList,
} from "lucide-react";
import { useTranslation } from "../services/translation.service";
import { useCart } from "../context/CartContext";
import { useTasks } from "../context/TasksContext";
import { authService } from "../services/auth.service";
import LanguageSwitcher from "./LanguageSwitcher";
import OpenTasksPopup from "./OpenTasksPopup";

// Haat Logo URL
const HAAT_LOGO_URL = "https://haatdaas.lan-wan.net/daas/images/logo.svg";
const MARKET_TUTORIAL_EVENT = "market-tutorial:open";
const MARKET_TUTORIAL_FORCE_START_KEY = "market_tutorial_force_start";
const MARKET_HOME_EVENT = "market:go-home";

const TASKS_BADGE_INTRO_KEY = "tasks_badge_intro_seen";

const loc = (language: string, he: string, ar: string, en: string) =>
  language === "he" ? he : language === "ar" ? ar : en;

const Header: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [showTasksPopup, setShowTasksPopup] = useState(false);
  const [showTasksIntro, setShowTasksIntro] = useState(false);
  const tasksButtonRef = useRef<HTMLDivElement>(null);

  const { t, changeLanguage, getAvailableLanguages, currentLanguage } =
    useTranslation();
  const { itemCount, lastAddedItem, clearLastAddedItem, logoutOtpSession, businessInfo } =
    useCart();
  const { tasks } = useTasks();
  const navigate = useNavigate();
  const location = useLocation();
  const logoutLabel = loc(
    currentLanguage,
    "התנתקות",
    "تسجيل الخروج",
    "Log out",
  );

  // Animate cart icon when item is added
  const [cartBounce, setCartBounce] = useState(false);
  useEffect(() => {
    if (lastAddedItem) {
      setCartBounce(true);
      const timer = setTimeout(() => {
        setCartBounce(false);
        clearLastAddedItem();
      }, 600);
      return () => clearTimeout(timer);
    }
  }, [lastAddedItem, clearLastAddedItem]);

  // Show "What's New" tasks badge intro tooltip for returning users who
  // have already seen the tutorial but haven't seen the tasks badge yet.
  useEffect(() => {
    if (!businessInfo?.haatBussID) return;
    const tutorialSeen =
      localStorage.getItem(`onboarding_seen_${businessInfo.haatBussID}`) === "1";
    const introSeen =
      localStorage.getItem(TASKS_BADGE_INTRO_KEY) === "1";
    if (tutorialSeen && !introSeen) {
      setShowTasksIntro(true);
    }
  }, [businessInfo?.haatBussID]);

  const dismissTasksIntro = () => {
    localStorage.setItem(TASKS_BADGE_INTRO_KEY, "1");
    setShowTasksIntro(false);
  };

  const goToCartPage = () => {
    navigate("/cart");
  };

  const handleLogout = async () => {
    try {
      await authService.logout();
    } finally {
      logoutOtpSession();
      navigate("/login", { replace: true });
    }
  };

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setIsMobileMenuOpen(false);
      setIsMobileNavOpen(false);
    };

    if (isMobileMenuOpen || isMobileNavOpen) {
      document.addEventListener("click", handleClickOutside);
      return () => document.removeEventListener("click", handleClickOutside);
    }
  }, [isMobileMenuOpen, isMobileNavOpen]);

  useEffect(() => {
    if (!showTasksPopup) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        tasksButtonRef.current &&
        !tasksButtonRef.current.contains(e.target as Node)
      ) {
        setShowTasksPopup(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [showTasksPopup]);

  const availableLanguages = getAvailableLanguages(businessInfo?.countryID);

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  // The logo is a "home" button: always land on the category grid. When we are
  // already on the market route the router won't remount it, so ask the market
  // view to clear its browse state instead.
  const handleLogoClick = () => {
    if (location.pathname === "/") {
      window.dispatchEvent(new Event(MARKET_HOME_EVENT));
      return;
    }
    navigate("/");
  };

  const handleOpenTutorial = () => {
    if (location.pathname !== "/") {
      sessionStorage.setItem(MARKET_TUTORIAL_FORCE_START_KEY, "1");
      navigate("/");
      return;
    }
    window.dispatchEvent(new Event(MARKET_TUTORIAL_EVENT));
  };

  return (
    <>
      <motion.header
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="sticky top-0 bg-white border-b border-gray-200 z-[110] transition-colors duration-300"
      >
        <div className="max-w-7xl mx-auto px-4 lg:px-4 xl:px-8">
          <div className="flex justify-between items-center h-16 min-w-0">
            {/* Logo and Brand - Haat Delivery */}
            <motion.div
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.1, duration: 0.3 }}
              className="flex items-center space-x-1 sm:space-x-2 rtl:space-x-reverse cursor-pointer min-w-0 flex-shrink"
              onClick={handleLogoClick}
            >
              <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center">
                <img
                  src={HAAT_LOGO_URL}
                  alt="Haat Delivery"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="hidden md:block min-w-0">
                <h1 className="text-sm md:text-base lg:text-lg font-semibold text-gray-900 truncate">
                  {t("header.title")}
                </h1>
              </div>
              <div className="block md:hidden min-w-0">
                <h1 className="text-xs font-semibold text-gray-900 truncate">
                  {t("header.title")}
                </h1>
              </div>
            </motion.div>

            {/* Center Navigation */}
            <nav className="hidden md:flex items-center gap-2">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleNavigation("/")}
                className={`px-3 py-2 rounded-none text-sm font-medium transition-colors border-b-2 ${
                  location.pathname === "/"
                    ? "text-[#dc2626] border-[#dc2626]"
                    : "text-gray-700 border-transparent hover:text-[#dc2626]"
                }`}
              >
                {t("addProduct.marketTab")}
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleNavigation("/manual-form")}
                className={`px-3 py-2 rounded-none text-sm font-medium transition-colors border-b-2 ${
                  location.pathname === "/manual-form"
                    ? "text-[#dc2626] border-[#dc2626]"
                    : "text-gray-700 border-transparent hover:text-[#dc2626]"
                }`}
              >
                {t("addProduct.manualTab")}
              </motion.button>
            </nav>

            {/* Right Side - Desktop actions + mobile controls */}
            <motion.div
              initial={{ x: 50, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.5 }}
              className="flex items-center gap-2 flex-shrink-0"
            >
              <motion.div className="hidden md:flex items-center gap-2">
                  <motion.div className="flex items-center rounded-xl border border-gray-200 bg-gray-50 p-1">
                    <LanguageSwitcher variant="header" />
                    <div className="h-5 w-px bg-gray-200" />

                  {/* Cart Icon - Desktop */}
                  <motion.button
                    data-tutorial-id="global-cart-button"
                    whileTap={{ scale: 0.95 }}
                    animate={
                      cartBounce
                        ? { scale: [1, 1.2, 1], rotate: [0, -10, 10, 0] }
                        : {}
                    }
                    transition={{ duration: 0.4 }}
                    onClick={goToCartPage}
                    className="relative flex h-9 w-9 items-center justify-center rounded-lg text-gray-600 hover:text-[#dc2626] transition-colors"
                    aria-label={t("header.cart")}
                  >
                    <ShoppingCart className="w-5 h-5" />
                    {itemCount > 0 && (
                      <motion.span
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center"
                      >
                        {itemCount > 99 ? "99+" : itemCount}
                      </motion.span>
                    )}
                  </motion.button>

                  <motion.div className="h-5 w-px bg-gray-200" />

                  {/* Tasks Badge - Desktop */}
                  <div className="relative" ref={tasksButtonRef}>
                    <motion.button
                      data-tutorial-id="tasks-badge-button"
                      whileTap={{ scale: 0.95 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        dismissTasksIntro();
                        setShowTasksPopup((prev) => !prev);
                      }}
                      className="relative flex h-9 w-9 items-center justify-center rounded-lg text-gray-600 hover:text-[#dc2626] transition-colors"
                      aria-label={t("tasks.popupTitle")}
                      title={t("tasks.popupTitle")}
                    >
                      {showTasksIntro && (
                        <span className="absolute inset-0 rounded-lg animate-ping bg-red-400 opacity-40 pointer-events-none" />
                      )}
                      <ClipboardList className="w-5 h-5" />
                      <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                        {tasks.length > 99 ? "99+" : tasks.length}
                      </span>
                    </motion.button>
                    <AnimatePresence>
                      {showTasksIntro && (
                        <motion.div
                          initial={{ opacity: 0, y: -6, scale: 0.95 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: -6, scale: 0.95 }}
                          transition={{ duration: 0.2 }}
                          className="absolute top-full mt-2 end-0 w-56 bg-white border border-red-200 rounded-xl shadow-lg p-3 z-[130] text-right"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <p className="text-xs font-semibold text-red-600 mb-1">
                            ✨ {t("tasks.introTitle")}
                          </p>
                          <p className="text-xs text-gray-600 leading-relaxed">
                            {t("tasks.introDescription")}
                          </p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                    <OpenTasksPopup
                      isOpen={showTasksPopup}
                      onClose={() => setShowTasksPopup(false)}
                    />
                  </div>

                  <motion.div className="h-5 w-px bg-gray-200" />

                  <motion.button
                    whileTap={{ scale: 0.95 }}
                    onClick={handleOpenTutorial}
                    className="relative flex h-9 w-9 items-center justify-center rounded-lg text-gray-600 hover:text-[#dc2626] transition-colors"
                    aria-label={t("tutorial.help")}
                    title={t("tutorial.help")}
                  >
                    <HelpCircle className="w-5 h-5" />
                  </motion.button>

                  <motion.div className="h-5 w-px bg-gray-200" />

                  {/* Logout - Desktop */}
                  <motion.button
                    whileTap={{ scale: 0.95 }}
                    onClick={handleLogout}
                    className="relative flex h-9 w-9 items-center justify-center rounded-lg text-gray-600 hover:text-[#dc2626] transition-colors"
                    aria-label={logoutLabel}
                    title={logoutLabel}
                  >
                    <LogOut className="w-5 h-5" />
                  </motion.button>
                </motion.div>
              </motion.div>

              {/* Mobile controls order: burger, cart, 3-dots */}
              <div className="md:hidden flex items-center gap-1" dir="ltr">
                <div className="relative">
                  <motion.button
                    whileTap={{ scale: 0.95 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsMobileNavOpen((prev) => !prev);
                      setIsMobileMenuOpen(false);
                    }}
                    className="flex relative p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    aria-label={
                      loc(currentLanguage, "תפריט ניווט", "قائمة التنقل", "Navigation menu")
                    }
                  >
                    <Menu className="w-5 h-5" />
                  </motion.button>

                  <AnimatePresence>
                    {isMobileNavOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: -8, scale: 0.96 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -8, scale: 0.96 }}
                        transition={{ duration: 0.18 }}
                        className="absolute left-0 mt-2 w-44 bg-white rounded-xl border border-gray-200 z-[120] p-2"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          type="button"
                          onClick={() => {
                            handleNavigation("/");
                            setIsMobileNavOpen(false);
                          }}
                          className={`w-full text-right px-3 py-2 rounded-lg text-sm ${
                            location.pathname === "/"
                              ? "bg-red-50 text-red-700"
                              : "text-gray-700 hover:bg-gray-100"
                          }`}
                        >
                          {t("addProduct.marketTab")}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            handleNavigation("/manual-form");
                            setIsMobileNavOpen(false);
                          }}
                          className={`w-full text-right px-3 py-2 rounded-lg text-sm ${
                            location.pathname === "/manual-form"
                              ? "bg-red-50 text-red-700"
                              : "text-gray-700 hover:bg-gray-100"
                          }`}
                        >
                          {t("addProduct.manualTab")}
                        </button>

                        <div className="my-1 h-px bg-gray-200" />

                        <motion.button
                          type="button"
                          onClick={() => {
                            setIsMobileNavOpen(false);
                            handleLogout();
                          }}
                          className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm text-[#dc2626] hover:bg-red-50"
                        >
                          <LogOut className="w-4 h-4" />
                          <span>
                            {logoutLabel}
                          </span>
                        </motion.button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Tasks Badge - Mobile */}
                <div className="relative">
                  <motion.button
                    data-tutorial-id="tasks-badge-button"
                    whileTap={{ scale: 0.95 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      dismissTasksIntro();
                      setShowTasksPopup((prev) => !prev);
                    }}
                    className="flex relative p-2 text-gray-600 rounded-lg transition-colors"
                    aria-label={t("tasks.popupTitle")}
                  >
                    {showTasksIntro && (
                      <span className="absolute inset-0 rounded-lg animate-ping bg-red-400 opacity-40 pointer-events-none" />
                    )}
                    <ClipboardList className="w-5 h-5" />
                    <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                      {tasks.length > 99 ? "99+" : tasks.length}
                    </span>
                  </motion.button>
                  <AnimatePresence>
                    {showTasksIntro && (
                      <motion.div
                        initial={{ opacity: 0, y: -6, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -6, scale: 0.95 }}
                        transition={{ duration: 0.2 }}
                        className="fixed inset-x-4 top-20 bg-white border border-red-200 rounded-xl shadow-lg p-3 z-[130] text-right"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <p className="text-xs font-semibold text-red-600 mb-1">
                          ✨ {t("tasks.introTitle")}
                        </p>
                        <p className="text-xs text-gray-600 leading-relaxed">
                          {t("tasks.introDescription")}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                  <OpenTasksPopup
                    isOpen={showTasksPopup}
                    onClose={() => setShowTasksPopup(false)}
                  />
                </div>

                <motion.button
                  data-tutorial-id="global-cart-button"
                  whileTap={{ scale: 0.95 }}
                  animate={
                    cartBounce
                      ? { scale: [1, 1.2, 1], rotate: [0, -10, 10, 0] }
                      : {}
                  }
                  transition={{ duration: 0.4 }}
                  onClick={goToCartPage}
                  className="flex relative p-2 text-gray-600 rounded-lg transition-colors flex-shrink-0"
                  aria-label={t("header.cart")}
                >
                  <ShoppingCart className="w-5 h-5" />
                  {itemCount > 0 && (
                    <motion.span
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center"
                    >
                      {itemCount > 99 ? "99+" : itemCount}
                    </motion.span>
                  )}
                </motion.button>

                <div className="relative">
                  <motion.button
                    whileTap={{ scale: 0.95 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsMobileMenuOpen((prev) => !prev);
                      setIsMobileNavOpen(false);
                    }}
                    className="flex relative p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    aria-label={
                      loc(currentLanguage, "אפשרויות", "قائمة الخيارات", "Options menu")
                    }
                  >
                    <MoreVertical className="w-5 h-5" />
                  </motion.button>

                  <AnimatePresence>
                    {isMobileMenuOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: -8, scale: 0.96 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -8, scale: 0.96 }}
                        transition={{ duration: 0.18 }}
                        className="absolute left-0 mt-2 w-40 bg-white rounded-xl border border-gray-200 z-[120] p-2"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          type="button"
                          onClick={() => {
                            handleOpenTutorial();
                            setIsMobileMenuOpen(false);
                          }}
                          className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm text-gray-700 hover:bg-gray-100"
                        >
                          <HelpCircle className="w-4 h-4" />
                          <span>{t("tutorial.help")}</span>
                        </button>

                            <div className="my-1 h-px bg-gray-200" />

                            {availableLanguages.map((language) => (
                              <button
                                key={language.code}
                                type="button"
                                onClick={() => {
                                  changeLanguage(language.code);
                                  setIsMobileMenuOpen(false);
                                }}
                                className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm ${
                                  currentLanguage === language.code
                                    ? "bg-red-50 text-red-700"
                                    : "text-gray-700 hover:bg-gray-100"
                                }`}
                              >
                                <span
                                  className={`w-1.5 h-1.5 rounded-full ${
                                    currentLanguage === language.code
                                      ? "bg-red-600"
                                      : "bg-transparent"
                                  }`}
                                />
                                <span>{language.name}</span>
                              </button>
                            ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.header>
    </>
  );
};

export default Header;
